import React from "react";
// Material ui core components
import Grid from "@material-ui/core/Grid";
// App components
import { Dashboard as DashboardLayout } from "../../layouts";
import CustomSlotsTable from "./CustomSlotsTable";
import CustomSlotsList from "./CustomSlotsList";
import CustomSlotSynonyms from "./CustomSlotSynonyms";

import { connect } from "react-redux";
import { pushMessage } from "../../redux/actions/commonActions";
import {
  listCustomSlotTypes,
  createNewCustomSlot,
  clearListOfCustomSlotTypes,
  deleteCustomSlotType,
  generateSkillBuilderCode,
  clearDeleteCustomSlotTypeResponse,
  clearCreatedCustomSlotTypeResponse,
  updateCustomSlotType,
  clearUpdateCustomSlotTypeResponse,
  clearAllCustomSlotsState
} from "../../redux/actions/customSlotsActions";

class CustomSlots extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedSlot: 0,
      selectedValue: "",
      selectedSlotCustomId: "",
      synonyms: [],
      selectedSlotTypeIdx: undefined,
      skillId: undefined
    };
  }

  componentWillUnmount() {
    this.props.clearAllCustomSlotsState();
  }

  componentDidMount() {
    this.loadCustomSlotTypes();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.loadCustomSlotTypes();

    // Check if a new custom slot type was created
    if (this.props.createNewCustomSlotTypeResponse) {
      this.handleApiResponse(this.props.createNewCustomSlotTypeResponse, "Custom slot type created");
      this.props.clearCreatedCustomSlotTypeResponse();
    }

    // Check if a custom slot type was deleted
    if (this.props.deleteCustomSlotTypeResponse) {
      this.handleApiResponse(this.props.deleteCustomSlotTypeResponse, "Custom slot type deleted");
      this.props.clearDeleteCustomSlotTypeResponse();
    }

    // Check if a custom slot type was updated
    if (this.props.updateCustomSlotTypeResponse) {
      this.handleApiResponse(this.props.updateCustomSlotTypeResponse, "Custom slot type updated");
      this.props.clearUpdateCustomSlotTypeResponse();
    }
  }

  loadCustomSlotTypes = () => {
    // Check if action which fetches skill (and skill ID) is done
    if (!this.state.skillId && this.props.skillId) {
      this.setState({ skillId: this.props.skillId });
      this.props.listCustomSlotTypes();
    }
  };

  resetSelectedCustomSlotType = () => {
    this.setState({
      selectedSlot: 0,
      selectedValue: "",
      synonyms: [],
      selectedSlotCustomId: "",
      selectedSlotTypeIdx: undefined
    });
  };

  handleApiResponse = (apiResponse, successMessage) => {
    if (
      apiResponse.code === 200 || // creating and updating custom slot type response
      apiResponse.status === "success" // deleting custom slot type response
    ) {
      this.props.generateSkillBuilderCode();

      this.props.pushMessage({
        type: "success",
        message: successMessage
      });
    } else {
      this.props.pushMessage({
        type: "error",
        message: apiResponse.message
      });
    }

    this.resetSelectedCustomSlotType();
    this.props.listCustomSlotTypes();
  };

  handleSelectSlot = index => {
    this.setState(prevState => ({
      ...prevState,
      selectedSlot: index,
      selectedValue: "",
      synonyms: []
    }));
  };

  handleSelectValue = rowData => {
    this.setState(prevState => ({
      ...prevState,
      selectedValue: rowData["value"],
      synonyms: rowData["synonyms"],
      selectedSlotCustomId: rowData["id"]
    }));
  };

  /**
   * Check if custom slot type name is valid.
   * NB: Validation logic taken from old portal (skill-id-custom-intent-slots-new.js).
   * @param {string} customSlotTypeName - Name of custom slot type to validate.
   * @return {Object} - Object with two keys: valid (boolean) and message (string).
   * @example { valid: false, message: "Name is already in use" }
   */
  validateCustomSlotTypeName = customSlotTypeName => {
    const result = { valid: true, message: "Custom slot type name valid" };
    const { listCustomSlotTypesResponse } = this.props;
    const customSlotTypeNameTrimmed = customSlotTypeName.trim();

    // Check if there are no existing custom slot types
    if (!listCustomSlotTypesResponse.custom_slots) {
      result.valid = false;
      result.message = "Could not load existing custom slot types";
      return result;
    }

    const existingCustomSlotTypeNames = listCustomSlotTypesResponse.custom_slots.map(
      customSlotType => customSlotType.slotName
    );
    const builtInSlotTypesNames = listCustomSlotTypesResponse.built_in_slots.map(
      builtInSlotType => builtInSlotType.slotName
    );

    // Check if name is an empty string or whitespace only
    if (customSlotTypeNameTrimmed.length === 0) {
      result.valid = false;
      result.message = "Name must not be empty";
      return result;
    }

    // Check for [A-Za-z_.] mismatch
    if (!/^[A-Za-z_.]+$/.test(customSlotTypeName)) {
      result.valid = false;
      result.message = "Name may only contain letters, periods and non-consecutive underscores";
      return result;
    }

    // Check for consecutive underscores
    if (customSlotTypeName.indexOf("__") !== -1) {
      result.valid = false;
      result.message = "Name must not contain consecutive underscores";
      return result;
    }

    // Check if name not unique among existing custom slot type names
    if (existingCustomSlotTypeNames.indexOf(customSlotTypeName) !== -1) {
      result.valid = false;
      result.message = "Name is already in use";
      return result;
    }

    // Check for unknown built-in slot type
    if (customSlotTypeName.startsWith("AMAZON.") && builtInSlotTypesNames.indexOf(customSlotTypeName) === -1) {
      result.valid = false;
      result.message = "Unknown name for built-in slot type";
      return result;
    }

    return result;
  };

  handleNewCustomSlotClick = newCustomSlotTypeName => {
    const validationResult = this.validateCustomSlotTypeName(newCustomSlotTypeName);

    if (!validationResult.valid) {
      return this.props.pushMessage({
        type: "error",
        message: validationResult.message
      });
    }

    this.props.pushMessage({
      type: "info",
      message: "Creating custom slot type"
    });

    this.props.clearListOfCustomSlotTypes();
    this.props.createNewCustomSlot(newCustomSlotTypeName);
  };

  handleDeleteCustomSlotClick = customSlotTypeId => {
    this.props.pushMessage({
      type: "info",
      message: "Deleting custom slot type"
    });
    this.props.clearListOfCustomSlotTypes();
    this.props.deleteCustomSlotType(customSlotTypeId);
  };

  reloadCustomSlotTypes = () => {
    this.props.clearListOfCustomSlotTypes();
    this.props.listCustomSlotTypes();
  };

  parseDbSlotValues = dbCustomSlotType => {
    return dbCustomSlotType.slotValue.map((slotValue, index) => {
      let value = "";
      let id = "";
      let synonyms = "";

      if (slotValue.includes("{")) {
        value = slotValue.split("[")[0];
        synonyms = slotValue.substring(slotValue.indexOf("[") + 1, slotValue.indexOf("]"));
        value = value.split("{")[0];
        id = slotValue.split("{")[1].slice(1, -2);
      } else {
        value = slotValue.split("[")[0];
        synonyms = slotValue.substring(slotValue.indexOf("[") + 1, slotValue.indexOf("]"));
      }
      synonyms = synonyms.split(", ").map(synonym =>
        synonym
          .trim()
          .replace(/^"/, "")
          .replace(/"$/, "")
      );
      return { value: value, id: id, synonyms: synonyms };
    });
  };

  render() {
    const { selectedSlot, selectedValue, synonyms, selectedSlotCustomId } = this.state;

    return (
      <DashboardLayout title="Profile">
        <Grid container spacing={5}>
          <Grid item lg={3} md={9} xs={12}>
            <CustomSlotsList
              selectedSlot={selectedSlot}
              handleSelectSlot={this.handleSelectSlot}
              createNewCustomSlot={this.handleNewCustomSlotClick}
              handleDeleteCustomSlotClick={this.handleDeleteCustomSlotClick}
              slotsLoaded={false}
            />
          </Grid>
          <Grid item lg={6} md={9} xs={12}>
            <CustomSlotsTable
              selectedSlot={selectedSlot}
              handleSelectValue={this.handleSelectValue}
              skillId={this.state.skillId}
              userAccessToken={this.props.userAccessToken}
              reloadCustomSlotTypes={this.reloadCustomSlotTypes}
              resetSelectedCustomSlotType={this.resetSelectedCustomSlotType}
              parseDbSlotValues={this.parseDbSlotValues}
            />
          </Grid>
          <Grid item lg={3} md={3} xs={12}>
            <CustomSlotSynonyms
              selectedValue={selectedValue}
              synonyms={synonyms}
              selectedSlotTypeIdx={selectedSlot}
              selectedSlotCustomId={selectedSlotCustomId}
              updateCustomSlotType={this.props.updateCustomSlotType}
              parseDbSlotValues={this.parseDbSlotValues}
            />
          </Grid>
        </Grid>
      </DashboardLayout>
    );
  }
}

const mapStateToProps = state => {
  return {
    listCustomSlotTypesResponse: state.customSlots.listCustomSlotTypesResponse,
    createNewCustomSlotTypeResponse: state.customSlots.createNewCustomSlotTypeResponse,
    skillId: state.skill.appId,
    userAccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
    deleteCustomSlotTypeResponse: state.customSlots.deleteCustomSlotTypeResponse,
    updateCustomSlotTypeResponse: state.customSlots.updateCustomSlotTypeResponse
  };
};

export default connect(
  mapStateToProps,
  {
    listCustomSlotTypes,
    createNewCustomSlot,
    clearListOfCustomSlotTypes,
    deleteCustomSlotType,
    generateSkillBuilderCode,
    pushMessage,
    clearDeleteCustomSlotTypeResponse,
    clearCreatedCustomSlotTypeResponse,
    updateCustomSlotType,
    clearUpdateCustomSlotTypeResponse,
    clearAllCustomSlotsState
  }
)(CustomSlots);
